import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import CasinoIcon from "@material-ui/icons/Casino"
import GetAppIcon from "@material-ui/icons/GetApp"
import HistoryIcon from "@material-ui/icons/History"
import SearchIcon from "@material-ui/icons/Search"
import WhatsAppIcon from "@material-ui/icons/WhatsApp"
import { useSnackbar } from "../../../utils/notificacoes"
import { usePosicaoClientesContext } from "../context/usePosicaoClientesContext"
import { PosicaoClientesActionsTypes } from "../context/posicaoClientesActions"
import type { ClienteRanking } from "../models/posicaoClientesModel"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "1000px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    subtitleText: {
        color: "#7f8c8d",
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(3),
    },
    filtrosRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        alignItems: "flex-end",
    },
    fieldWrapper: {
        flex: "1 1 180px",
        minWidth: 150,
    },
    pesquisarBtn: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        fontWeight: 600,
        padding: "10px 24px",
        boxShadow: "0 2px 8px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
    exportarBtn: {
        borderRadius: 25,
        padding: "10px 24px",
        fontWeight: 600,
    },
    sortearBtn: {
        borderRadius: 25,
        backgroundColor: "#2196f3",
        color: "white",
        fontWeight: 600,
        padding: "10px 24px",
        boxShadow: "0 2px 8px rgba(33,150,243,0.3)",
        "&:hover": { backgroundColor: "#1565c0" },
    },
    totalText: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(2),
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 180,
    },
    emailCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 180,
    },
    acaoCell: {
        whiteSpace: "nowrap",
    },
    whatsappBtn: {
        color: "#25d366",
    },
    sortudoNome: {
        fontWeight: 700,
        fontSize: "1.4rem",
        color: "#2c3e50",
        textAlign: "center",
        marginTop: theme.spacing(2),
    },
    sortudoTelefone: {
        color: "#7f8c8d",
        textAlign: "center",
        marginTop: theme.spacing(1),
    },
}))

function linkWhatsapp(cliente: ClienteRanking): string {
    const base = "https://api.whatsapp.com/send?phone=%s&text=%s"
    let tel = "55" + (cliente.telefone ?? "")
    tel = tel.replace(/[\s\-().+]/g, "")
    const texto = encodeURIComponent(`Olá ${cliente.nome}! `)
    return base.replace("%s", tel).replace("%s", texto)
}

function gerarCsv(clientes: ClienteRanking[], tipoRanking: number): void {
    const cabecalho = ["idCliente", "nome", "telefone", "email",
        tipoRanking === 0 ? "totalSelos" : tipoRanking === 2 ? "quantidadePedidos" : "valorCompras"]
    const linhas = clientes.map(c => [
        String(c.id),
        c.nome ?? "",
        c.telefone ?? "",
        c.email ?? "",
        tipoRanking === 0
            ? String(c.totalSelos ?? "")
            : tipoRanking === 2
            ? String(c.quantidadePedidos ?? "")
            : String(c.totalCompras ?? ""),
    ])
    const csv = [cabecalho, ...linhas].map(row => row.join(";")).join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "RankingClientes.csv"
    a.click()
    URL.revokeObjectURL(url)
}

export function RankingClientes() {
    const classes = useStyles()
    const { dados, dispatch, pesquisar, erroCarregamento } = usePosicaoClientesContext()
    const { enqueueSnackbar } = useSnackbar()
    const [sortearAberto, setSortearAberto] = useState(false)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const handlePesquisar = async () => {
        if (!dados.filtro.dataInicio || !dados.filtro.dataFinal) {
            alertStart("Informe as datas de início e fim.", "error")
            return
        }
        await pesquisar()
    }

    const handleSortear = () => {
        if (dados.clientes.length === 0) {
            alertStart("Realize uma pesquisa antes de sortear.", "error")
            return
        }
        dispatch({ type: PosicaoClientesActionsTypes.SORTEAR })
        setSortearAberto(true)
    }

    const colMetrica = (c: ClienteRanking): string => {
        if (dados.filtro.tipoRanking === 0) return c.totalSelos != null ? String(c.totalSelos) : "-"
        if (dados.filtro.tipoRanking === 2) return c.quantidadePedidos != null ? String(c.quantidadePedidos) : "-"
        return c.totalCompras ?? "-"
    }

    const nomeColMetrica = () => {
        if (dados.filtro.tipoRanking === 0) return "Total de Selos"
        if (dados.filtro.tipoRanking === 2) return "Qtd. Pedidos"
        return "Valor Total"
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Ranking de Clientes
                </Typography>
                <Typography variant="body2" className={classes.subtitleText}>
                    Filtre e visualize os clientes que mais realizam pedidos no seu estabelecimento.
                </Typography>

                {/* Card de Filtros */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Filtros de Busca
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.filtrosRow}>
                            <Box className={classes.fieldWrapper}>
                                <TextField
                                    label="Data inicial"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    placeholder="dd/mm/aaaa"
                                    value={dados.filtro.dataInicio}
                                    onChange={e => {
                                        const value = e.target.value
                                        dispatch({
                                            type: PosicaoClientesActionsTypes.ATUALIZAR_FILTRO,
                                            payload: { campo: "dataInicio", valor: value },
                                        })
                                    }}
                                />
                            </Box>
                            <Box className={classes.fieldWrapper}>
                                <TextField
                                    label="Data final"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    placeholder="dd/mm/aaaa"
                                    value={dados.filtro.dataFinal}
                                    onChange={e => {
                                        const value = e.target.value
                                        dispatch({
                                            type: PosicaoClientesActionsTypes.ATUALIZAR_FILTRO,
                                            payload: { campo: "dataFinal", valor: value },
                                        })
                                    }}
                                />
                            </Box>
                            <Box className={classes.fieldWrapper}>
                                <TextField
                                    select
                                    label="Tipo de Ranking"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.filtro.tipoRanking}
                                    onChange={e => {
                                        const value = Number(e.target.value)
                                        dispatch({
                                            type: PosicaoClientesActionsTypes.ATUALIZAR_FILTRO,
                                            payload: { campo: "tipoRanking", valor: value },
                                        })
                                    }}
                                >
                                    <MenuItem value={0}>Quantidade de Selos</MenuItem>
                                    <MenuItem value={1}>Valor Total das Compras</MenuItem>
                                    <MenuItem value={2}>Quantidade de Pedidos</MenuItem>
                                </TextField>
                            </Box>
                            <Box className={classes.fieldWrapper}>
                                <TextField
                                    select
                                    label="Filtrar clientes"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.filtro.apenasPrimeiraCompra}
                                    onChange={e => {
                                        const value = Number(e.target.value)
                                        dispatch({
                                            type: PosicaoClientesActionsTypes.ATUALIZAR_FILTRO,
                                            payload: { campo: "apenasPrimeiraCompra", valor: value },
                                        })
                                    }}
                                >
                                    <MenuItem value={0}>Todos os clientes</MenuItem>
                                    <MenuItem value={1}>Apenas clientes com primeira compra no período</MenuItem>
                                </TextField>
                            </Box>
                        </Box>
                        <Box display="flex" style={{ gap: 12, marginTop: 20, flexWrap: "wrap" }}>
                            <Button
                                variant="contained"
                                className={classes.pesquisarBtn}
                                startIcon={<SearchIcon />}
                                onClick={handlePesquisar}
                            >
                                Pesquisar
                            </Button>
                            <Button
                                variant="outlined"
                                className={classes.exportarBtn}
                                startIcon={<GetAppIcon />}
                                disabled={dados.clientes.length === 0}
                                onClick={() => gerarCsv(dados.clientes, dados.filtro.tipoRanking)}
                            >
                                Exportar CSV
                            </Button>
                            <Button
                                variant="contained"
                                className={classes.sortearBtn}
                                startIcon={<CasinoIcon />}
                                onClick={handleSortear}
                            >
                                Sortear
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Card de Resultados */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Resultados
                        </Typography>
                        <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                            Total: {dados.clientes.length} clientes
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <TableContainer style={{ maxHeight: 500, overflowX: "auto" }}>
                            <Table size="small" stickyHeader style={{ width: "100%", minWidth: 690 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ minWidth: 160 }}>Nome</TableCell>
                                        <TableCell style={{ minWidth: 120 }}>Telefone</TableCell>
                                        <TableCell style={{ minWidth: 200 }}>E-mail</TableCell>
                                        <TableCell style={{ minWidth: 120 }}>{nomeColMetrica()}</TableCell>
                                        <TableCell style={{ minWidth: 90 }} align="center">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dados.clientes.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" style={{ color: "#7f8c8d", padding: 24 }}>
                                                Aplique os filtros e clique em Pesquisar para ver os resultados.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        dados.clientes.map((cliente, idx) => (
                                            <TableRow key={cliente.id} hover>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={cliente.nome}
                                                >
                                                    {idx + 1}. {cliente.nome}
                                                </TableCell>
                                                <TableCell>{cliente.telefone ?? "-"}</TableCell>
                                                <TableCell
                                                    className={classes.emailCell}
                                                    title={cliente.email}
                                                >
                                                    {cliente.email}
                                                </TableCell>
                                                <TableCell>{colMetrica(cliente)}</TableCell>
                                                <TableCell align="center" className={classes.acaoCell}>
                                                    <Tooltip title="Histórico">
                                                        <IconButton
                                                            size="small"
                                                            href={`/historico-cliente/${cliente.id}`}
                                                        >
                                                            <HistoryIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="WhatsApp">
                                                        <IconButton
                                                            size="small"
                                                            className={classes.whatsappBtn}
                                                            href={linkWhatsapp(cliente)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <WhatsAppIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Paper>
            </Box>

            {/* Dialog Sorteio */}
            <Dialog open={sortearAberto} onClose={() => setSortearAberto(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Sorteio</DialogTitle>
                <DialogContent style={{ textAlign: "center", paddingBottom: 32 }}>
                    <CasinoIcon style={{ fontSize: 64, color: "#2196f3", marginBottom: 8 }} />
                    {dados.sortudo ? (
                        <>
                            <Typography className={classes.sortudoNome}>{dados.sortudo.nome}</Typography>
                            {dados.sortudo.telefone && (
                                <Typography className={classes.sortudoTelefone}>
                                    {dados.sortudo.telefone}
                                </Typography>
                            )}
                        </>
                    ) : (
                        <Typography style={{ color: "#7f8c8d" }}>Nenhum cliente sorteado.</Typography>
                    )}
                    <Box style={{ marginTop: 24 }}>
                        <Button
                            variant="contained"
                            style={{ backgroundColor: "#2196f3", color: "white", borderRadius: 25, marginRight: 12 }}
                            startIcon={<CasinoIcon />}
                            onClick={() => dispatch({ type: PosicaoClientesActionsTypes.SORTEAR })}
                        >
                            Sortear novamente
                        </Button>
                        <Button variant="outlined" style={{ borderRadius: 25 }} onClick={() => setSortearAberto(false)}>
                            Fechar
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    )
}
